/**
 * Check-printing formatting helpers shared by the payroll check template
 * (PayRollReviewPage) and the bank-account MICR preview (BankAccountList).
 *
 * MICR notes: the strings returned by formatMicrLine are meant to be rendered
 * with the E-13B "MICR" font (public/fonts/micrenc.ttf), whose glyph mapping
 * is A = Transit (⑆), B = Amount (⑇), C = On-Us (⑈), D = Dash (⑉).
 */

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

// Scale words for each 3-digit group, low to high.
const SCALE = ["", " Thousand", " Million", " Billion"];

function convertHundreds(n: number): string {
  if (n === 0) return "";
  if (n >= 100) {
    const rem = n % 100;
    return `${ONES[Math.floor(n / 100)]} Hundred${
      rem > 0 ? " " + convertHundreds(rem) : ""
    }`;
  }
  if (n >= 20) {
    const rem = n % 10;
    return `${TENS[Math.floor(n / 10)]}${rem > 0 ? " " + ONES[rem] : ""}`;
  }
  return ONES[n];
}

/**
 * Convert a dollar amount to the legal ("written") amount line, e.g.
 * 1234.56 -> "One Thousand Two Hundred Thirty Four and 56/100".
 *
 * Works on integer cents so float noise (e.g. 148.29999999999998) and the
 * "cents round to 100" edge (99.995 -> "Ninety Nine and 100/100") can never
 * produce a legal amount that disagrees with the courtesy amount.
 *
 * Returns "" for amounts that must never appear on a check (negative,
 * non-finite, or beyond the supported scale) — callers are expected to block
 * printing such checks (see hasCompleteMicrData / print guards).
 */
export function amountToWords(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return "";
  const totalCents = Math.round(amount * 100);
  const dollars = Math.floor(totalCents / 100);
  const cents = totalCents % 100;

  let words: string;
  if (dollars === 0) {
    words = "Zero";
  } else {
    // Split into 3-digit groups, low to high.
    const groups: number[] = [];
    let n = dollars;
    while (n > 0) {
      groups.push(n % 1000);
      n = Math.floor(n / 1000);
    }
    if (groups.length > SCALE.length) return ""; // > $999,999,999,999
    words = groups
      .map((g, i) => (g > 0 ? convertHundreds(g) + SCALE[i] : ""))
      .filter(Boolean)
      .reverse()
      .join(" ");
  }
  return `${words} and ${String(cents).padStart(2, "0")}/100`;
}

/** Digits only — for the transit (routing) field and check number. */
function digitsOnly(value: string | number): string {
  return String(value).replace(/\D/g, "");
}

/**
 * On-us (account) field: digits plus the E-13B dash symbol. Hyphens are
 * mapped to "D" (the dash glyph in micrenc.ttf); everything else non-numeric
 * (spaces, etc.) is stripped since E-13B has no glyph for it.
 */
function toMicrOnUs(value: string): string {
  return String(value)
    .replace(/[^\d-]/g, "")
    .replace(/-/g, "D");
}

/**
 * Build the MICR line for a business check, rendered right-to-left on paper:
 *   ⑈check#⑈ ⑆routing⑆ account⑈
 * (auxiliary on-us, transit, on-us — the amount field is intentionally
 * omitted; the bank of first deposit encodes it). Fields are separated by a
 * single space so that, with the transit field anchored at X9.100-160-1
 * positions 33-43 (see micrRightOffsetIn), the aux on-us symbol lands at
 * position 45 — the same layout measured on a bank-accepted Checkeeper
 * reference check.
 *
 * The check number is intentionally NOT zero-padded: the serial in the MICR
 * line must read identically to the printed corner number and the check
 * number in the positive-pay issue file, all of which use the raw value —
 * string-matching positive-pay systems treat "0990" vs "990" as an
 * exception. The reference check prints its serial unpadded too.
 */
export function formatMicrLine(
  routing: string,
  account: string,
  checkNumber: string | number,
): string {
  const r = digitsOnly(routing);
  const a = toMicrOnUs(account);
  const c = digitsOnly(checkNumber);
  return `C${c}C A${r}A ${a}C`;
}

/**
 * CSS `right` offset, in inches from the check's right edge, for the
 * right-anchored MICR line produced by formatMicrLine.
 *
 * Anchors the transit field at X9.100-160-1 positions 33-43 — its rightmost
 * symbol's right edge 4.3125" from the document edge (position N's right
 * edge sits 0.3125" + (N-1) x 0.125" in). The line's right end is the on-us
 * symbol, which trails the transit field by one space + account + symbol,
 * i.e. (2 + account length) characters at 0.125" each. A 9-digit account
 * yields 2.9375", matching a measured Checkeeper reference check.
 *
 * Clamped at 1.9375" so the on-us symbol can never cross position 14 (the
 * boundary of the amount field, positions 1-12, which must stay clear for
 * the bank's encoding) even with an unusually long account number.
 */
export function micrRightOffsetIn(account: string): number {
  const a = toMicrOnUs(account);
  return Math.max(4.3125 - (2 + a.length) * 0.125, 1.9375);
}

/**
 * ABA routing number checksum (weights 3-7-1). True only for a 9-digit
 * number whose weighted sum is divisible by 10.
 */
export function isValidRoutingNumber(
  routing: string | null | undefined,
): boolean {
  const r = digitsOnly(routing ?? "");
  if (r.length !== 9) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = weights.reduce((acc, w, i) => acc + w * Number(r[i]), 0);
  return sum % 10 === 0;
}

/**
 * Whether a check has everything required to print a complete, bank-readable
 * MICR line. A check failing this must not be printed — a partial MICR line
 * guarantees manual repair (or rejection) at the bank.
 */
export function hasCompleteMicrData(
  routing: string | null | undefined,
  account: string | null | undefined,
  checkNumber: string | number | null | undefined,
): boolean {
  return (
    isValidRoutingNumber(routing) &&
    toMicrOnUs(account ?? "").length > 0 &&
    checkNumber != null &&
    digitsOnly(checkNumber).length > 0
  );
}
